import axios, { AxiosResponse } from 'axios';
import { 
  User, 
  LoginRequest, 
  RegisterRequest, 
  TokenResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserFilters,
  Product, 
  ProductFormData, 
  ProductFilters,
  Budget, 
  BudgetVersion,
  BudgetFormData, 
  BudgetFilters,
  PaginatedResponse,
  ApiError 
} from '../types';

// Configuración base de axios
const API_BASE_URL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Permitir envío de cookies
});

// Variables para controlar el refresh de tokens
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para manejar errores
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class ApiService {
  /**
   * Crea una nueva versión del presupuesto con los datos y items actuales
   */
  createVersion = async (id: number, versionData: any): Promise<any> => {
    const response = await axiosInstance.post(`/budgets/${id}/versions`, versionData);
    return response.data;
  };
  // Estadísticas de productos
  productStats = {
    getMostUsed: async (limit: number = 10): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get(`/products/stats/most-used`, { params: { limit } });
      return response.data;
    },
    getWithoutStock: async (limit: number = 50): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get(`/products/stats/without-stock`, { params: { limit } });
      return response.data;
    },
    getInactive: async (limit: number = 50): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get(`/products/stats/inactive`, { params: { limit } });
      return response.data;
    },
  };
  // Configurar token de autorización (se mantiene en memoria, no en localStorage)
  setAuthToken(token: string | null) {
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  }

  // Métodos genéricos HTTP
  async get<T>(url: string, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.put(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.patch(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.delete(url, config);
    return response.data;
  }

  // Endpoints de autenticación
  auth = {
    login: async (credentials: LoginRequest): Promise<TokenResponse> => {
      const response: AxiosResponse<TokenResponse> = await axiosInstance.post('/auth/login', credentials);
      return response.data;
    },

    register: async (userData: RegisterRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.post('/auth/register', userData);
      return response.data;
    },

    getCurrentUser: async (): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.get('/auth/me');
      return response.data;
    },

    logout: async (): Promise<{message: string}> => {
      const response: AxiosResponse<{message: string}> = await axiosInstance.post('/auth/logout', {});
      return response.data;
    },
  };

  // Endpoints de usuarios (admin only)
  users = {
    getAll: async (filters: UserFilters = {}): Promise<User[]> => {
      const response: AxiosResponse<User[]> = await axiosInstance.get('/users', {
        params: filters
      });
      return response.data;
    },

    getById: async (id: number): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.get(`/users/${id}`);
      return response.data;
    },

    create: async (userData: UserCreateRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.post('/users', userData);
      return response.data;
    },

    update: async (id: number, userData: UserUpdateRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.put(`/users/${id}`, userData);
      return response.data;
    },

    delete: async (id: number): Promise<{message: string}> => {
      const response: AxiosResponse<{message: string}> = await axiosInstance.delete(`/users/${id}`);
      return response.data;
    },

    updateProfile: async (userData: UserUpdateRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.put('/users/me', userData);
      return response.data;
    },

    changePassword: async (userId: number, newPassword: string): Promise<{message: string}> => {
      const response: AxiosResponse<{message: string}> = await axiosInstance.post(
        `/users/${userId}/change-password`,
        { user_id: userId, new_password: newPassword }
      );
      return response.data;
    },
  };

  // Endpoints de productos
  products = {
    getAll: async (filters: ProductFilters = {}): Promise<PaginatedResponse<Product>> => {
      const response: AxiosResponse<PaginatedResponse<Product>> = await axiosInstance.get('/products', {
        params: filters
      });
      return response.data;
    },

    getById: async (id: number): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosInstance.get(`/products/${id}`);
      return response.data;
    },

    create: async (productData: ProductFormData): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosInstance.post('/products', productData);
      return response.data;
    },

    update: async (id: number, productData: ProductFormData): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosInstance.put(`/products/${id}`, productData);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await axiosInstance.delete(`/products/${id}`);
    },

    deletePermanently: async (id: number): Promise<{message: string, soft_delete: boolean}> => {
      const response = await axiosInstance.delete(`/products/${id}/permanent`);
      return response.data;
    },

    search: async (query: string): Promise<Product[]> => {
      const response: AxiosResponse<Product[]> = await axiosInstance.get(`/products/search`, {
        params: { q: query }
      });
      return response.data;
    },

    deleteAll: async (): Promise<{ message: string }> => {
      const response: AxiosResponse<{ message: string }> = await axiosInstance.delete('/products/all');
      return response.data;
    },
  };

  // Endpoints de presupuestos
  budgets = {
  createVersion: this.createVersion,
    /**
     * Preview de cálculo de ítems de presupuesto (centralizado en backend)
     * @param items Lista de items a calcular (product_id, metros_cuadrados, con_desperdicio, con_estructura, ...)
     * @returns Lista de items calculados (con cantidad, subtotal, estructura, etc)
     */
    previewCalculation: async (items: Array<{
      product_id: number;
      metros_cuadrados: number;
      con_desperdicio?: boolean;
      con_estructura?: boolean;
      con_terminaciones?: boolean;
      con_instalacion?: boolean;
      is_structure?: boolean;
    }>): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.post('/budget/preview', { items });
      return response.data;
    },
    getAll: async (filters: BudgetFilters = {}): Promise<PaginatedResponse<Budget>> => {
      const response: AxiosResponse<PaginatedResponse<Budget>> = await axiosInstance.get('/budgets', {
        params: filters
      });
      return response.data;
    },

    getById: async (id: number): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${id}`);
      return response.data;
    },

    create: async (budgetData: BudgetFormData): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.post('/budgets', budgetData);
      return response.data;
    },

    update: async (id: number, versionData: any): Promise<BudgetVersion> => {
      const response: AxiosResponse<BudgetVersion> = await axiosInstance.put(`/budgets/${id}`, versionData);
      return response.data;
    },

    updateClientInfo: async (id: number, clientData: { client_name: string; client_email: string }): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.patch(`/budgets/${id}/client`, clientData);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await axiosInstance.delete(`/budgets/${id}`);
    },

    duplicate: async (id: number): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.post(`/budgets/${id}/duplicate`);
      return response.data;
    },

    addItem: async (id: number, item: { 
      product_id: number; 
      metros_cuadrados: number;
      con_desperdicio?: boolean;
      con_estructura?: boolean;
    }): Promise<Budget> => {
      // En lugar de agregar un item directamente, creamos una nueva versión con el item agregado
      // Primero obtenemos el budget actual
      const currentBudget = await axiosInstance.get(`/budgets/${id}`);
      const budget = currentBudget.data;
      
      // Obtenemos la versión actual
      const currentVersion = budget.versions?.find((v: any) => v.is_current) || budget.versions?.[0];
      
      if (!currentVersion) {
        throw new Error('No se encontró versión actual del presupuesto');
      }
      
      // Creamos una nueva versión con todos los items existentes más el nuevo
      const newVersionData = {
        title: currentVersion.title,
        notes: currentVersion.notes || '',
        vendor_name: currentVersion.vendor_name,
        vendor_email: currentVersion.vendor_email,
        sections_count: currentVersion.sections_count || 1,
        items: [
          ...(currentVersion.items || []).map((existingItem: any) => ({
            product_id: existingItem.product_id,
            metros_cuadrados: existingItem.metros_cuadrados,
            con_desperdicio: existingItem.con_desperdicio || false,
            con_estructura: existingItem.con_estructura || false,
            con_terminaciones: existingItem.con_terminaciones || false,
            con_instalacion: existingItem.con_instalacion || false,
            order_index: existingItem.order_index || 0,
            is_structure: existingItem.is_structure === true // Si ya viene marcado, lo respeta
          })),
          {
            product_id: item.product_id,
            metros_cuadrados: item.metros_cuadrados,
            con_desperdicio: item.con_desperdicio || false,
            con_estructura: item.con_estructura || false,
            con_terminaciones: false,
            con_instalacion: false,
            order_index: (currentVersion.items?.length || 0),
            is_structure: false // El ítem principal siempre explícitamente
          }
        ]
      };
      
      const response: AxiosResponse<any> = await axiosInstance.post(`/budgets/${id}/versions`, newVersionData);
      
      // Después de crear la nueva versión, obtenemos el budget actualizado
      const updatedBudgetResponse: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${id}`);
      return updatedBudgetResponse.data;
    },

    removeItem: async (budgetId: number, itemId: number): Promise<Budget> => {
      // Para eliminar un item, creamos una nueva versión sin ese item
      // Primero obtenemos el budget actual
      const currentBudget = await axiosInstance.get(`/budgets/${budgetId}`);
      const budget = currentBudget.data;
      
      // Obtenemos la versión actual
      const currentVersion = budget.versions?.find((v: any) => v.is_current) || budget.versions?.[0];
      
      if (!currentVersion) {
        throw new Error('No se encontró versión actual del presupuesto');
      }
      
      // Filtramos los items excluyendo el que queremos eliminar
      const filteredItems = (currentVersion.items || [])
        .filter((existingItem: any) => existingItem.id !== itemId)
        .map((existingItem: any) => ({
          product_id: existingItem.product_id,
          metros_cuadrados: existingItem.metros_cuadrados,
          cantidad: existingItem.cantidad,
          con_desperdicio: existingItem.con_desperdicio || false,
          con_estructura: existingItem.con_estructura || false,
          con_terminaciones: existingItem.con_terminaciones || false,
          con_instalacion: existingItem.con_instalacion || false,
          order_index: existingItem.order_index || 0
        }));
      
      // Creamos una nueva versión sin el item eliminado
      const newVersionData = {
        title: currentVersion.title,
        notes: currentVersion.notes || '',
        vendor_name: currentVersion.vendor_name,
        vendor_email: currentVersion.vendor_email,
        sections_count: currentVersion.sections_count || 1,
        items: filteredItems
      };
      
      const response: AxiosResponse<any> = await axiosInstance.post(`/budgets/${budgetId}/versions`, newVersionData);
      
      // Después de crear la nueva versión, obtenemos el budget actualizado
      const updatedBudgetResponse: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${budgetId}`);
      return updatedBudgetResponse.data;
    },

    addStructureItem: async (id: number, item: {
      descripcion: string;
      unidad: string;
      cantidad: number;
      precio_unitario: number;
    }): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.post(`/budgets/${id}/structure-items`, item);
      return response.data;
    },

    removeStructureItem: async (budgetId: number, itemId: number): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.delete(`/budgets/${budgetId}/structure-items/${itemId}`);
      return response.data;
    },

    updateStatus: async (id: number, status: string): Promise<Budget> => {
  const response: AxiosResponse<Budget> = await axiosInstance.patch(`/budgets/${id}/status`, { status });
  return response.data;
    },

    exportPdf: async (id: number, versionId?: number): Promise<Blob> => {
      const url = versionId 
        ? `/budgets/${id}/export/pdf?version_id=${versionId}`
        : `/budgets/${id}/export/pdf`;
      const response: AxiosResponse<Blob> = await axiosInstance.get(url, {
        responseType: 'blob'
      });
      return response.data;
    },

    exportExcel: async (startDate: string, endDate: string): Promise<Blob> => {
      const response: AxiosResponse<Blob> = await axiosInstance.get('/budgets/export/excel', {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      return response.data;
    },

    getStats: async () => {
      const response = await axiosInstance.get('/budgets/stats');
      return response.data;
    },

    getAdvancedStats: async () => {
      const response = await axiosInstance.get('/budgets/advanced-stats');
      return response.data;
    },

    getUserActivityStats: async (limit: number = 10) => {
      const response = await axiosInstance.get('/budgets/user-activity-stats', {
        params: { limit }
      });
      return response.data;
    },

    getUserSalesStats: async (limit: number = 10) => {
      const response = await axiosInstance.get('/budgets/user-sales-stats', {
        params: { limit }
      });
      return response.data;
    },
  };
}

// Instancia singleton del servicio
export const apiService = new ApiService();

// Exportar axiosInstance para uso compartido
export { axiosInstance };

// Configurar token al inicializar si existe en localStorage
const token = localStorage.getItem('token');
if (token) {
  apiService.setAuthToken(token);
}

export default apiService;
